import DialogProvider from "@components/dialog_provider"
import TextInput from "@components/text_input"
import { oneTimesUpdate } from "@domain/coupons"
import { yupResolver } from "@hookform/resolvers/yup"
import useControlledForm from "@hooks/use_controlled_form"
import useTranslate from "@hooks/use_translate"
import { Stack } from "@mui/material"
import { ScriptLimitFormData } from "@pages/main/scripts"
import { showErrorToast, showSuccessToast } from "@utils/toast"
import { OneTimesType } from "__generated__/graphql"
import { useState } from "react"
import * as yup from "yup"

type AddNewLimitsDialogProps = {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
}

export default function AddNewLimitsDialog({
  isOpen,
  setIsOpen
}: AddNewLimitsDialogProps) {
  const { translate } = useTranslate()
  const [loading, setLoading] = useState(false)
  const schema = yup
    .object({
      fieldName: yup.string().required(),
      usage: yup.number().required(),
      limit: yup.number().required(),
      plan: yup.string().required()
    })
    .required()

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    trigger,
    formState: { isValid }
  } = useControlledForm<ScriptLimitFormData>({
    resolver: yupResolver(schema),
    mode: "all"
  })

  const setClose = () => {
    setIsOpen(false)
  }

  const handleClose = () => {
    setIsOpen(false)
    reset()
  }

  async function onSubmit(data: ScriptLimitFormData) {
    setLoading(true)
    const {
      isSuccess,
      errorMessage,
      data: responseData
    } = await oneTimesUpdate({
      type: OneTimesType.AddToCustomerProduct,
      metaData: {
        fieldName: data.fieldName,
        plan: data.plan,
        limit: data.limit,
        usage: data.usage
      }
    })

    if (!isSuccess || !responseData) {
      setLoading(false)
      showErrorToast(String(errorMessage))
      return
    }

    showSuccessToast(translate("succesfully_added"))
    setLoading(false)
    handleClose()
  }

  return (
    <DialogProvider
      title={translate("customer_products_add_new_limit")}
      leftButton={{
        text: translate("cancel"),
        color: "secondary",
        onClick: setClose,
        disabled: loading
      }}
      rightButton={{
        text: translate("create"),
        color: "primary",
        onClick: handleSubmit(onSubmit),
        loading: loading,
        disabled: !isValid
      }}
      isOpen={isOpen}
      setClose={handleClose}
      size="small"
    >
      <Stack gap={1}>
        <TextInput<ScriptLimitFormData>
          label={translate("field_name")}
          disabled={loading}
          name="fieldName"
          placeholder={translate("enter")}
          control={control}
          onChange={(e) => {
            setValue("fieldName", e.target.value)
            trigger("fieldName")
          }}
          required
        />
        <TextInput<ScriptLimitFormData>
          type="number"
          label={translate("usage")}
          disabled={loading}
          name="usage"
          placeholder={translate("enter")}
          onChange={(e) => {
            setValue("usage", Number(e.target.value))
            trigger("usage")
          }}
          control={control}
          required
        />
        <TextInput<ScriptLimitFormData>
          type="number"
          label={"Limit"}
          disabled={loading}
          name="limit"
          onChange={(e) => {
            setValue("limit", Number(e.target.value))
            trigger("limit")
          }}
          placeholder={translate("enter")}
          control={control}
          required
        />
        <TextInput<ScriptLimitFormData>
          type="select"
          label={translate("plan")}
          disabled={loading}
          name="plan"
          control={control}
          placeholder={translate("select")}
          onChange={(e) => {
            setValue("plan", String(e.target.value))
            trigger("plan")
          }}
          selectOptions={[
            { label: "FreeTrial", value: "FreeTrial" },
            { label: "Start", value: "Start" },
            { label: "Grow", value: "Grow" },
            { label: "Scale", value: "Scale" },
            { label: "Special", value: "Special" },
            { label: "All", value: "All" }
          ]}
          required
        />
      </Stack>
    </DialogProvider>
  )
}
